<template>
    <div class="container h-full">
        <div class="overflow-hidden rounded-md">
            <div class="px-4 py-5 space-y-6 bg-white">
                <!-- this block will be shown only if editing and edit data has at least one image -->
                <div
                    v-if="editData && editData.images.length"
                    class="relative flex items-center justify-center group"
                >
                    <!-- show icons on hover with blurry background -->
                    <div class="absolute hidden w-full h-full group-hover:flex">
                        <!-- blurry background -->
                        <div class="w-full h-full bg-black rounded opacity-50" />

                        <!-- replace icon -->
                        <div
                            class="absolute"
                            style="top: calc(50% - 16px); left: calc(50% - 32px)"
                            @click="replaceImage"
                        >
                            <ReplaceIcon class="w-8 text-white cursor-pointer fill-current" />
                        </div>

                        <!-- delete icon -->
                        <div
                            class="absolute"
                            style="top: calc(50% - 16px); left: calc(50% + 32px)"
                            @click="deleteImage(editData.images[0])"
                        >
                            <DeleteIcon class="w-8 text-red-500 cursor-pointer fill-current" />
                        </div>
                    </div>

                    <div class="h-56">
                        <!-- if editData had image and new one was selected then this is that new image's preview -->
                        <img
                            v-if="imagePreview"
                            class="object-cover object-center max-w-full max-h-full rounded"
                            :src="imagePreview"
                            :alt="editData.name"
                        />

                        <!-- if editData has image then this is the image that is rendered at first on edit view -->
                        <img
                            v-if="!imagePreview && editData.images.length"
                            class="object-cover object-center max-w-full max-h-full rounded"
                            :src="editData.images[0].image_path"
                            :alt="editData.name"
                        />
                    </div>

                    <input
                        ref="brand-image-upload"
                        class="hidden"
                        type="file"
                        @change="filesChange($event.target.files)"
                    />
                </div>

                <!-- Brand name -->
                <div>
                    <div class="block text-sm font-medium text-gray-700">Brand Name*</div>
                    <div class="flex flex-col mt-1 rounded-md shadow-sm">
                        <t-input
                            v-model="form.name"
                            placeholder="Brand Name"
                            :variant="errors('name') ? 'danger' : ''"
                        />
                        <div
                            v-if="errors('name')"
                            class="text-red-500"
                        >
                            {{ errors('name') }}
                        </div>
                    </div>
                </div>

                <!-- Belongs To Category -->
                <div>
                    <div class="block text-sm font-medium text-gray-700">Belongs to category*</div>
                    <div class="flex flex-col mt-1 rounded-md shadow-sm">
                        <t-rich-select
                            v-model="form.categoryId"
                            :options="categoriesList"
                            :clearable="true"
                            value-attribute="id"
                            text-attribute="name"
                            placeholder="Select Category"
                            :variant="errors('category_id') ? 'danger' : ''"
                        />
                        <div
                            v-if="errors('category_id')"
                            class="text-red-500"
                        >
                            {{ errors('category_id') }}
                        </div>
                    </div>
                </div>

                <div v-if="!editData || !editData.images.length">
                    <FileUpload @filesUploaded="attachFilesToForm" />
                    <div
                        v-if="errors('image')"
                        class="text-red-500"
                    >
                        {{ errors('image') }}
                    </div>
                </div>
            </div>

            <div class="px-4 py-3 text-right bg-gray-50">
                <t-button
                    variant="success"
                    :disabled="processing"
                    @click="submitForm"
                >
                    Save
                </t-button>
            </div>
        </div>
    </div>
</template>

<script>
    import FileUpload from './FileUpload.vue';
    import Brand from '../Models/Brand';
    import DeleteIcon from './Icons/DeleteIcon.vue';
    import ReplaceIcon from './Icons/ReplaceIcon.vue';

    export default {
        name: 'BrandsCreate',

        components: {
            FileUpload,
            DeleteIcon,
            ReplaceIcon,
        },

        props: {
            editData: {
                type: Object,
                default: null,
            },
        },

        data() {
            return {
                form: new Brand(),
                brandsList: [],
                categoriesList: [],
                imagePreview: null,
                processing: false,
            };
        },

        async mounted() {
            this.fetchBrands();
            this.fetchCategories();
            if (this.editData) {
                this.form.name = this.editData.name;
                this.form.categoryId = this.editData.category_id;
            }
        },

        methods: {
            submitForm() {
                const payload = this.buildPayload();
                this.editData ? this.update(payload) : this.save(payload);
            },

            buildPayload() {
                const formData = new FormData();
                if (this.form.name) {
                    formData.append('name', this.form.name);
                }
                if (this.form.categoryId) {
                    formData.append('category_id', this.form.categoryId);
                }
                if (this.form.image) {
                    formData.append('image', this.form.image);
                }
                if (this.editData) {
                    formData.append('_method', 'PUT');
                }
                return formData;
            },

            save(payload) {
                this.processing = true;
                this.$emit('set-processing', true);
                this.$inertia.post(route('brands.store'), payload, {
                    onSuccess: () => {
                        this.$emit('transactionComplete');
                    },
                    onFinish: () => {
                        this.processing = false;
                        this.$emit('set-processing', false);
                    },
                });
            },

            update(payload) {
                this.processing = true;
                this.$emit('set-processing', true);
                this.$inertia.post(route('brands.update', this.editData.id), payload, {
                    onSuccess: () => {
                        this.$emit('transactionComplete');
                    },
                    onFinish: () => {
                        this.processing = false;
                        this.$emit('set-processing', false);
                    },
                });
            },

            attachFilesToForm(files) {
                this.form.image = files[0];
            },

            replaceImage() {
                this.$refs['brand-image-upload'].click();
            },

            filesChange(fileList) {
                if (!fileList.length) return;
                this.form.image = fileList[0];
                this.getImagePreviews();
            },

            deleteImage(image) {
                this.$inertia.delete(route('images.delete', image.id));
            },

            getImagePreviews() {
                if (/\.(jpe?g|png|gif)$/i.test(this.form.image.name)) {
                    const reader = new FileReader();
                    reader.addEventListener(
                        'load',
                        () => {
                            this.imagePreview = reader.result;
                        },
                        false,
                    );

                    reader.readAsDataURL(this.form.image);
                }
            },

            fetchBrands() {
                this.$axios.get(route('brands.index')).then((res) => {
                    this.brandsList = res.data.brands;
                });
            },

            fetchCategories() {
                this.$axios.get(route('categories.parent')).then((res) => {
                    this.categoriesList = res.data.categories;
                });
            },
        },
    };
</script>
